import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../App';
import { 
  Users, 
  PhoneCall, 
  Package, 
  ShieldCheck, 
  Plus, 
  Clock, 
  CheckCircle2, 
  Wrench, 
  UserCheck, 
  Search,
  ChevronRight,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface Visitor {
  id?: string;
  name: string;
  organization: string;
  purpose: string;
  hostName: string;
  checkInTime: string;
  checkOutTime?: string;
  status: 'active' | 'checked_out';
}

interface CallLog {
  id?: string;
  date: string;
  callerName: string;
  phone: string;
  message: string;
  targetEmployee: string;
  status: 'pending' | 'resolved';
}

interface PackageLog {
  id?: string;
  date: string;
  carrier: string;
  recipientName: string;
  status: 'received' | 'delivered';
}

interface ToolBooking {
  id?: string;
  technicianName: string;
  toolName: string;
  quantity: number;
  checkoutDate: string;
  expectedReturn: string;
  status: 'checked_out' | 'returned';
}

export default function ReceptionistDashboard() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'visitors' | 'calls' | 'packages' | 'tools'>('visitors');

  // Firestore state
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [packages, setPackages] = useState<PackageLog[]>([]);
  const [toolBookings, setToolBookings] = useState<ToolBooking[]>([]);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [isVisitorModalOpen, setIsVisitorModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);

  // Forms
  const [visitorForm, setVisitorForm] = useState({
    name: '',
    organization: '',
    purpose: '',
    hostName: ''
  });

  const [callForm, setCallForm] = useState({
    callerName: '',
    phone: '',
    message: '',
    targetEmployee: ''
  });

  const [packageForm, setPackageForm] = useState({
    carrier: '',
    recipientName: ''
  });

  const [toolForm, setToolForm] = useState({
    technicianName: '',
    toolName: '',
    quantity: 1,
    expectedReturn: format(new Date(Date.now() + 86400000 * 3), 'yyyy-MM-dd') // 3 days from now
  });

  // Pre-defined available tools list for receptionist store booking
  const availableToolsList = [
    "Bosch Hammer Drill",
    "Yellow Jacket Vacuum Pump",
    "Fluke Digital Multimeter",
    "R410A Refrigerant Manifold Gauge",
    "Fiberglass Step Ladder 8ft",
    "AC Recovery Machine",
    "Leak Detector Torch",
    "Pipe Cutter & Flaring Tool Kit"
  ];

  // Subscribe
  useEffect(() => {
    const unsubVisitors = onSnapshot(
      query(collection(db, 'receptionist_visitors'), orderBy('checkInTime', 'desc')),
      (snapshot) => {
        const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Visitor));
        setVisitors(loaded);
        if (loaded.length === 0) {
          // Seed some default visitor records for aesthetic preview
          const defaults: Visitor[] = [
            { name: 'Eng. Christopher Mallya', organization: 'CRDB Bank Plc', purpose: 'Scheduled Boardroom Meeting', hostName: 'Jackson Mandela (MD)', checkInTime: format(new Date(), 'yyyy-MM-dd HH:mm'), status: 'active' },
            { name: 'Diana Lowassa', organization: 'Tigo Tanzania', purpose: 'Invoicing & Contract Discussion', hostName: 'Sarah John (Accounts)', checkInTime: '2026-07-22 10:15', checkOutTime: '2026-07-22 11:30', status: 'checked_out' }
          ];
          defaults.forEach(v => addDoc(collection(db, 'receptionist_visitors'), v));
        }
      }
    );

    const unsubCalls = onSnapshot(
      query(collection(db, 'receptionist_calls'), orderBy('date', 'desc')),
      (snapshot) => {
        setCalls(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CallLog)));
      }
    );

    const unsubPackages = onSnapshot(
      query(collection(db, 'receptionist_packages'), orderBy('date', 'desc')),
      (snapshot) => {
        setPackages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PackageLog)));
      }
    );

    const unsubTools = onSnapshot(
      query(collection(db, 'receptionist_store_bookings'), orderBy('checkoutDate', 'desc')),
      (snapshot) => {
        setToolBookings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ToolBooking)));
      }
    );

    return () => {
      unsubVisitors();
      unsubCalls();
      unsubPackages();
      unsubTools();
    };
  }, []);

  // Handlers
  const handleCheckInVisitor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitorForm.name || !visitorForm.hostName) {
      toast.error('Please enter visitor name and host');
      return;
    }
    try {
      await addDoc(collection(db, 'receptionist_visitors'), {
        ...visitorForm,
        checkInTime: format(new Date(), 'yyyy-MM-dd HH:mm'),
        status: 'active'
      });
      toast.success('Visitor checked in successfully');
      setIsVisitorModalOpen(false);
      setVisitorForm({ name: '', organization: '', purpose: '', hostName: '' });
    } catch (err) {
      console.error(err);
      toast.error('Failed to log visitor');
    }
  };

  const handleCheckOutVisitor = async (id: string) => {
    try {
      await updateDoc(doc(db, 'receptionist_visitors', id), {
        status: 'checked_out',
        checkOutTime: format(new Date(), 'yyyy-MM-dd HH:mm')
      });
      toast.success('Visitor checked out');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update check-out');
    }
  };

  const handleLogCall = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!callForm.callerName || !callForm.phone) {
      toast.error('Caller details are required');
      return;
    }
    try {
      await addDoc(collection(db, 'receptionist_calls'), {
        ...callForm,
        date: format(new Date(), 'yyyy-MM-dd HH:mm'),
        status: 'pending'
      });
      toast.success('Call log saved successfully');
      setIsCallModalOpen(false);
      setCallForm({ callerName: '', phone: '', message: '', targetEmployee: '' });
    } catch (err) {
      console.error(err);
      toast.error('Failed to log call');
    }
  };

  const handleResolveCall = async (id: string) => {
    try {
      await updateDoc(doc(db, 'receptionist_calls', id), { status: 'resolved' });
      toast.success('Call message marked as resolved/forwarded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update call status');
    }
  };

  const handleLogPackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!packageForm.carrier || !packageForm.recipientName) {
      toast.error('Please enter carrier and recipient details');
      return;
    }
    try {
      await addDoc(collection(db, 'receptionist_packages'), {
        ...packageForm,
        date: format(new Date(), 'yyyy-MM-dd HH:mm'),
        status: 'received'
      });
      toast.success('Package delivery logged');
      setIsPackageModalOpen(false);
      setPackageForm({ carrier: '', recipientName: '' });
    } catch (err) {
      console.error(err);
      toast.error('Failed to log package');
    }
  };

  const handleDeliverPackage = async (id: string) => {
    try {
      await updateDoc(doc(db, 'receptionist_packages', id), { status: 'delivered' });
      toast.success('Package marked as handed over to recipient');
    } catch (err) {
      console.error(err);
      toast.error('Failed to complete delivery');
    }
  };

  const handleBookTool = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!toolForm.technicianName || !toolForm.toolName) {
      toast.error('Please fill in technician and tool details');
      return;
    }
    try {
      await addDoc(collection(db, 'receptionist_store_bookings'), {
        ...toolForm,
        checkoutDate: format(new Date(), 'yyyy-MM-dd'),
        status: 'checked_out'
      });
      toast.success('Tool checked out from receptionist store');
      setIsToolModalOpen(false);
      setToolForm({
        technicianName: '',
        toolName: '',
        quantity: 1,
        expectedReturn: format(new Date(Date.now() + 86400000 * 3), 'yyyy-MM-dd')
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to register tool checkout');
    }
  };

  const handleReturnTool = async (id: string) => {
    try {
      await updateDoc(doc(db, 'receptionist_store_bookings', id), { status: 'returned' });
      toast.success('Tool marked as returned to store');
    } catch (err) {
      console.error(err);
      toast.error('Failed to return tool');
    }
  };

  // Filtered lists
  const filteredVisitors = useMemo(() => {
    return visitors.filter(v => 
      v.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      v.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.hostName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [visitors, searchQuery]);

  const filteredCalls = useMemo(() => {
    return calls.filter(c => 
      c.callerName.toLowerCase().includes(searchQuery.toLowerCase()) || 
      c.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.targetEmployee.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [calls, searchQuery]);

  const filteredPackages = useMemo(() => {
    return packages.filter(p => 
      p.carrier.toLowerCase().includes(searchQuery.toLowerCase()) || 
      p.recipientName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [packages, searchQuery]);

  const filteredTools = useMemo(() => {
    return toolBookings.filter(b => 
      b.technicianName.toLowerCase().includes(searchQuery.toLowerCase()) || 
      b.toolName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [toolBookings, searchQuery]);

  // Metrics
  const metrics = useMemo(() => {
    return {
      activeVisitors: visitors.filter(v => v.status === 'active').length,
      pendingCalls: calls.filter(c => c.status === 'pending').length,
      undeliveredPackages: packages.filter(p => p.status === 'received').length,
      checkedOutTools: toolBookings.filter(t => t.status === 'checked_out').length
    };
  }, [visitors, calls, packages, toolBookings]);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 bg-slate-50 min-h-screen">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        <div>
          <span className="px-3 py-1 bg-teal-50 text-teal-800 rounded-full text-xs font-black uppercase tracking-widest">
            Front Desk & Operations HOD
          </span>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-2">Receptionist Dashboard</h1>
          <p className="text-slate-500 font-medium">Coordinate office check-ins, record messages, manage incoming parcels, and track receptionist-held tools store.</p>
        </div>
        
        <div className="flex gap-3">
          {activeTab === 'visitors' && (
            <button 
              onClick={() => setIsVisitorModalOpen(true)}
              className="px-6 py-3.5 bg-teal-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-teal-600/20 active:scale-95 transition-all"
            >
              <UserCheck className="w-5 h-5" />
              Check-In Visitor
            </button>
          )}
          {activeTab === 'calls' && (
            <button 
              onClick={() => setIsCallModalOpen(true)}
              className="px-6 py-3.5 bg-sky-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-sky-600/20 active:scale-95 transition-all"
            >
              <PhoneCall className="w-5 h-5" />
              Log Telephone Call
            </button>
          )}
          {activeTab === 'packages' && (
            <button 
              onClick={() => setIsPackageModalOpen(true)}
              className="px-6 py-3.5 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
            >
              <Package className="w-5 h-5" />
              Receive Package
            </button>
          )}
          {activeTab === 'tools' && (
            <button 
              onClick={() => setIsToolModalOpen(true)}
              className="px-6 py-3.5 bg-blue-900 text-white rounded-2xl font-black text-sm uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-blue-900/20 active:scale-95 transition-all"
            >
              <Wrench className="w-5 h-5" />
              Store Tool Checkout
            </button>
          )}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-teal-50 text-teal-600 rounded-2xl">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Visitors Checked-In</span>
            <span className="text-2xl font-black text-slate-800">{metrics.activeVisitors} Active</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-sky-50 text-sky-600 rounded-2xl">
            <PhoneCall className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Unresolved Messages</span>
            <span className="text-2xl font-black text-slate-800">{metrics.pendingCalls} Calls</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-indigo-50 text-indigo-600 rounded-2xl">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Awaiting Collection</span>
            <span className="text-2xl font-black text-slate-800">{metrics.undeliveredPackages} Parcels</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-blue-50 text-blue-900 rounded-2xl">
            <Wrench className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Tools Checked-Out</span>
            <span className="text-2xl font-black text-slate-800">{metrics.checkedOutTools} Active Loans</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-2">
        <button 
          onClick={() => { setActiveTab('visitors'); setSearchQuery(''); }}
          className={`px-6 py-3.5 font-bold text-sm uppercase tracking-wider border-b-4 transition-all ${activeTab === 'visitors' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          Visitor Register
        </button>
        <button 
          onClick={() => { setActiveTab('calls'); setSearchQuery(''); }}
          className={`px-6 py-3.5 font-bold text-sm uppercase tracking-wider border-b-4 transition-all ${activeTab === 'calls' ? 'border-sky-600 text-sky-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          Call Logs
        </button>
        <button 
          onClick={() => { setActiveTab('packages'); setSearchQuery(''); }}
          className={`px-6 py-3.5 font-bold text-sm uppercase tracking-wider border-b-4 transition-all ${activeTab === 'packages' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          Deliveries/Parcels
        </button>
        <button 
          onClick={() => { setActiveTab('tools'); setSearchQuery(''); }}
          className={`px-6 py-3.5 font-bold text-sm uppercase tracking-wider border-b-4 transition-all relative ${activeTab === 'tools' ? 'border-blue-900 text-blue-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          🛠️ Tools Store
        </button>
      </div>

      {/* Controls */}
      <div className="p-6 bg-white rounded-3xl shadow-sm border border-slate-100 flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input 
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all text-sm"
          />
        </div>
        <div className="text-xs font-black uppercase text-slate-400 tracking-widest">
          Live Data Updates Connected
        </div>
      </div>

      {/* Main Lists */}
      {activeTab === 'visitors' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider text-[11px] font-black">
                  <th className="py-4 px-6">Visitor Name</th>
                  <th className="py-4 px-6">Organization</th>
                  <th className="py-4 px-6">Purpose of Visit</th>
                  <th className="py-4 px-6">Host / Employee</th>
                  <th className="py-4 px-6">Check-In</th>
                  <th className="py-4 px-6">Check-Out</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredVisitors.map(vis => (
                  <tr key={vis.id} className="hover:bg-slate-50/50">
                    <td className="py-4 px-6 font-bold text-slate-800">{vis.name}</td>
                    <td className="py-4 px-6 font-semibold text-slate-600">{vis.organization || 'Personal'}</td>
                    <td className="py-4 px-6 text-slate-500 font-medium">{vis.purpose}</td>
                    <td className="py-4 px-6 font-semibold text-slate-700">{vis.hostName}</td>
                    <td className="py-4 px-6 text-xs text-slate-500 font-bold">{vis.checkInTime}</td>
                    <td className="py-4 px-6 text-xs text-slate-500 font-bold">{vis.checkOutTime || '—'}</td>
                    <td className="py-4 px-6">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
                        vis.status === 'active' ? 'bg-teal-50 text-teal-800' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${vis.status === 'active' ? 'bg-teal-500' : 'bg-slate-400'}`} />
                        {vis.status === 'active' ? 'Inside Office' : 'Departed'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {vis.status === 'active' ? (
                        <button 
                          onClick={() => handleCheckOutVisitor(vis.id!)}
                          className="px-3.5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                        >
                          Check Out
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No Actions</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredVisitors.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-slate-400 italic">No visitors registered today.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'calls' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider text-[11px] font-black">
                  <th className="py-4 px-6">Call Time</th>
                  <th className="py-4 px-6">Caller Details</th>
                  <th className="py-4 px-6">Message / Instruction</th>
                  <th className="py-4 px-6">Target Recipient</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredCalls.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50/50">
                    <td className="py-4 px-6 text-xs text-slate-500 font-bold">{c.date}</td>
                    <td className="py-4 px-6">
                      <div className="font-bold text-slate-800">{c.callerName}</div>
                      <div className="text-slate-500 font-semibold text-xs">{c.phone}</div>
                    </td>
                    <td className="py-4 px-6 text-slate-600 font-medium max-w-xs truncate" title={c.message}>{c.message}</td>
                    <td className="py-4 px-6 font-semibold text-slate-700">{c.targetEmployee}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
                        c.status === 'resolved' ? 'bg-emerald-50 text-emerald-800' : 'bg-sky-50 text-sky-800'
                      }`}>
                        {c.status === 'resolved' ? 'Passed / Done' : 'Pending Callback'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {c.status === 'pending' ? (
                        <button 
                          onClick={() => handleResolveCall(c.id!)}
                          className="px-3.5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                        >
                          Mark Forwarded
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No Actions</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredCalls.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400 italic">No calls logged today.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'packages' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider text-[11px] font-black">
                  <th className="py-4 px-6">Date Received</th>
                  <th className="py-4 px-6">Courier / Carrier</th>
                  <th className="py-4 px-6">Recipient Name</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredPackages.map(pkg => (
                  <tr key={pkg.id} className="hover:bg-slate-50/50">
                    <td className="py-4 px-6 text-xs text-slate-500 font-bold">{pkg.date}</td>
                    <td className="py-4 px-6 font-bold text-slate-800">{pkg.carrier}</td>
                    <td className="py-4 px-6 font-semibold text-slate-700">{pkg.recipientName}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
                        pkg.status === 'delivered' ? 'bg-emerald-50 text-emerald-800' : 'bg-indigo-50 text-indigo-800'
                      }`}>
                        {pkg.status === 'delivered' ? 'Handed Over' : 'Awaiting Pickup'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {pkg.status === 'received' ? (
                        <button 
                          onClick={() => handleDeliverPackage(pkg.id!)}
                          className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                        >
                          Mark Delivered
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Delivered</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredPackages.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400 italic">No packages logged.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'tools' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider text-[11px] font-black">
                  <th className="py-4 px-6">Checkout Date</th>
                  <th className="py-4 px-6">Technician Name</th>
                  <th className="py-4 px-6">Tool Borrowed</th>
                  <th className="py-4 px-6 text-center">Qty</th>
                  <th className="py-4 px-6">Expected Return</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredTools.map(loan => (
                  <tr key={loan.id} className="hover:bg-slate-50/50">
                    <td className="py-4 px-6 text-xs text-slate-500 font-bold">{loan.checkoutDate}</td>
                    <td className="py-4 px-6 font-bold text-slate-800">{loan.technicianName}</td>
                    <td className="py-4 px-6 font-semibold text-blue-900">{loan.toolName}</td>
                    <td className="py-4 px-6 text-center font-bold text-slate-700">{loan.quantity}</td>
                    <td className="py-4 px-6 text-xs font-bold text-amber-600">{loan.expectedReturn || 'N/A'}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
                        loan.status === 'returned' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                      }`}>
                        {loan.status === 'returned' ? 'Returned to Store' : 'In Use'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {loan.status === 'checked_out' ? (
                        <button 
                          onClick={() => handleReturnTool(loan.id!)}
                          className="px-3.5 py-2 bg-blue-900 hover:bg-blue-950 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                        >
                          Mark Returned
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 italic">In Store</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredTools.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400 italic">No active tool store bookings found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODALS */}
      {/* 1. Check-In Visitor Modal */}
      {isVisitorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-lg p-8 shadow-2xl relative">
            <h2 className="text-xl font-black text-slate-800 mb-6">Check-In Office Visitor</h2>
            <form onSubmit={handleCheckInVisitor} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Visitor Full Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Salim Rashid"
                  required
                  value={visitorForm.name}
                  onChange={(e) => setVisitorForm({ ...visitorForm, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Company / Organization</label>
                <input 
                  type="text" 
                  placeholder="e.g. Halotel Tanzania"
                  value={visitorForm.organization}
                  onChange={(e) => setVisitorForm({ ...visitorForm, organization: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Purpose of Visit</label>
                <input 
                  type="text" 
                  placeholder="e.g. Dropping off documents / Board Meeting"
                  required
                  value={visitorForm.purpose}
                  onChange={(e) => setVisitorForm({ ...visitorForm, purpose: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Host Employee</label>
                <input 
                  type="text" 
                  placeholder="e.g. Jackson Mandela"
                  required
                  value={visitorForm.hostName}
                  onChange={(e) => setVisitorForm({ ...visitorForm, hostName: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsVisitorModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-teal-600 text-white rounded-xl font-bold text-sm"
                >
                  Check In
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Log Call Modal */}
      {isCallModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-lg p-8 shadow-2xl relative">
            <h2 className="text-xl font-black text-slate-800 mb-6">Log Telephone Message</h2>
            <form onSubmit={handleLogCall} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Caller Name</label>
                  <input 
                    type="text" 
                    placeholder="Caller Name"
                    required
                    value={callForm.callerName}
                    onChange={(e) => setCallForm({ ...callForm, callerName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Phone Number</label>
                  <input 
                    type="tel" 
                    placeholder="Phone"
                    required
                    value={callForm.phone}
                    onChange={(e) => setCallForm({ ...callForm, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Target Recipient</label>
                <input 
                  type="text" 
                  placeholder="e.g. Jackson Mandela"
                  required
                  value={callForm.targetEmployee}
                  onChange={(e) => setCallForm({ ...callForm, targetEmployee: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Message Detail / Note</label>
                <textarea 
                  placeholder="Details of the call/instruction..."
                  required
                  rows={3}
                  value={callForm.message}
                  onChange={(e) => setCallForm({ ...callForm, message: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none text-sm"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsCallModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-sky-600 text-white rounded-xl font-bold text-sm"
                >
                  Log Call
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Receive Package Modal */}
      {isPackageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-lg p-8 shadow-2xl relative">
            <h2 className="text-xl font-black text-slate-800 mb-6">Log Received Package</h2>
            <form onSubmit={handleLogPackage} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Courier / Carrier (e.g. DHL, EMS, Bolt)</label>
                <input 
                  type="text" 
                  placeholder="e.g. DHL Express Courier"
                  required
                  value={packageForm.carrier}
                  onChange={(e) => setPackageForm({ ...packageForm, carrier: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Recipient Employee Name</label>
                <input 
                  type="text" 
                  placeholder="Name of recipient staff"
                  required
                  value={packageForm.recipientName}
                  onChange={(e) => setPackageForm({ ...packageForm, recipientName: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsPackageModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold text-sm"
                >
                  Save Log
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Book / Checkout Tool Modal */}
      {isToolModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-lg p-8 shadow-2xl relative">
            <h2 className="text-xl font-black text-slate-800 mb-6">🛠️ Tools Store Checkout</h2>
            <form onSubmit={handleBookTool} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Technician Full Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Baraka Elias"
                  required
                  value={toolForm.technicianName}
                  onChange={(e) => setToolForm({ ...toolForm, technicianName: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Choose Tool</label>
                <select 
                  required
                  value={toolForm.toolName}
                  onChange={(e) => setToolForm({ ...toolForm, toolName: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none text-slate-800"
                >
                  <option value="">-- Select Tool from Store --</option>
                  {availableToolsList.map((tool, idx) => (
                    <option key={idx} value={tool}>{tool}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Quantity</label>
                  <input 
                    type="number" 
                    required
                    min={1}
                    value={toolForm.quantity}
                    onChange={(e) => setToolForm({ ...toolForm, quantity: Number(e.target.value) })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Expected Return Date</label>
                  <input 
                    type="date" 
                    required
                    value={toolForm.expectedReturn}
                    onChange={(e) => setToolForm({ ...toolForm, expectedReturn: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsToolModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-blue-900 text-white rounded-xl font-bold text-sm uppercase tracking-wider"
                >
                  Hand-out Tool
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
